import PaginatedResponse, { Paginated } from '_shared/_api/pagination/responses/paginated-response';
import { api } from '_shared/_stores/api';
import Guid from '_shared/guids/guid';
import UserNetworkConnectionSchema, { UserNetworkConnectionJSON } from 'users/networking/connections/_schemas/user-network-connection-schema';
import NetworkInvitationAPIRequest from 'users/networking/connections/invitations/_api/commands/network-invitation-api-command';
import NetworkInvitationQuery from 'users/networking/connections/invitations/_api/queries/network-invitation-query';
import NetworkInvitationInfoSchema, { NetworkInvitationInfoJSON } from 'users/networking/connections/invitations/_schemas/network-invitation-info-schema';
import z from 'zod';

export const userNetworkInvitationApi = api.injectEndpoints({
  endpoints: (build) => ({
    // #region Get
    getUserNetworkInvitations: (
      build.query<Paginated<NetworkInvitationInfoJSON>, NetworkInvitationQuery>({
        query: (query: NetworkInvitationQuery) => ({
          url: 'users/network-invitations',
          params: query.searchParams
        }),
        rawResponseSchema: z.array(NetworkInvitationInfoSchema.NetworkInvitationInfoJSONZ),
        transformResponse: (response: NetworkInvitationInfoJSON[], meta): Paginated<NetworkInvitationInfoJSON> => {
          const headers = meta?.response?.headers.get('x-pagination') ?? '';
          return {
            data: response,
            headers: PaginatedResponse.parsePaginationHeader(headers)
          };
        },
        providesTags: (result) =>
          result
            ? [
              // Tag each item
              ...result?.data.map(invitation => ({
                type: "NetworkInvitation" as const,
                id: invitation.id,
              })) ?? [],
              // Tag the list itself
              { type: "NetworkInvitation" as const, id: "LIST" },
            ]
            : [{ type: "NetworkInvitation" as const, id: "LIST" }],
      })
    ),
    // #endregion

    // #region Create
    createNetworkInvitation: (
      build.mutation<NetworkInvitationInfoJSON, NetworkInvitationAPIRequest>({
        query: (query: NetworkInvitationAPIRequest) => ({
          url: `users/network-invitations`,
          method: 'POST',
          body: query
        }),
        rawResponseSchema: NetworkInvitationInfoSchema.NetworkInvitationInfoJSONZ,
        invalidatesTags: [{
          type: 'NetworkInvitation',
          id: 'LIST'
        }]
      })
    ),
    // #endregion

    // #region Other
    cancelNetworkInvitation: (
      build.mutation<void, { invitationId: Guid }>({
        query: ({ invitationId }) => ({
          url: `users/network-invitations/${invitationId.value}/cancel`,
          method: 'POST'
        }),
        invalidatesTags: (result, error, id) => [
          {
            type: 'NetworkInvitation',
            id: id.invitationId.value
          },
          {
            type: 'NetworkInvitation',
            id: 'LIST'
          }
        ]
      })
    ),
    acceptNetworkInvitation: (
      build.mutation<UserNetworkConnectionJSON, { invitationId: Guid }>({
        query: ({ invitationId }) => ({
          url: `users/network-invitations/${invitationId.value}/accept`,
          method: 'POST'
        }),
        rawResponseSchema: UserNetworkConnectionSchema.UserNetworkConnectionJSONZ,
        invalidatesTags: (result, error, id) => [
          {
            type: 'NetworkInvitation',
            id: id.invitationId.value
          },
          {
            type: 'NetworkInvitation',
            id: 'LIST'
          }
        ]
      })
    ),
    rejectNetworkInvitation: (
      build.mutation<void, { invitationId: Guid }>({
        query: ({ invitationId }) => ({
          url: `users/network-invitations/${invitationId.value}/reject`,
          method: 'POST'
        }),
        invalidatesTags: (result, error, id) => [
          {
            type: 'NetworkInvitation',
            id: id.invitationId.value
          },
          {
            type: 'NetworkInvitation',
            id: 'LIST'
          }
        ]
      })
    ),
  })
});

export const {
  useGetUserNetworkInvitationsQuery,
  useCreateNetworkInvitationMutation,
  useCancelNetworkInvitationMutation,
  useAcceptNetworkInvitationMutation,
  useRejectNetworkInvitationMutation
} = userNetworkInvitationApi;
